// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/hooks/useAuth.tsx
//  Context de autenticação usando JWT da API Udisys.
// ════════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';

// ── Tipos ─────────────────────────────────────────────────────────────────

export interface EmpresaInfo {
  id: number;
  tenantId: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpjCpf?: string;
}

export interface UdisysUser {
  id: number;
  login: string;
  nome: string;
  email?: string;
  tenantId: string;
  empresa: EmpresaInfo;
  perfis: string[];
  isAdminGlobal: boolean;
}

/// Bloqueio de assinatura devolvido pelo gate do login (402/403): o front mostra
/// o aviso e, quando houver checkoutUrl, o botão de pagamento.
export interface BloqueioAssinatura {
  codigo: 'TRIAL_EXPIRADO' | 'ASSINATURA_SUSPENSA' | 'ASSINATURA_CANCELADA';
  mensagem: string;
  checkoutUrl?: string | null;
}

interface AuthContextType {
  user: UdisysUser | null;
  token: string | null;
  loading: boolean;
  signIn: (login: string, senha: string, empresaId?: number) => Promise<{ error: Error | null }>;
  signOut: () => void;
  getEmpresasDisponiveis: (login: string, senha: string) =>
    Promise<{ empresas: EmpresaInfo[]; error: Error | null; bloqueio?: BloqueioAssinatura }>;
  /// Empresas ativas do usuário logado (para o seletor de troca em sessão).
  minhasEmpresas: () => Promise<EmpresaInfo[]>;
  /// Troca a empresa ativa sem redigitar senha (re-emite o token + recarrega).
  trocarEmpresa: (empresaId: number) => Promise<{ error: Error | null }>;
}

// ── Chaves do localStorage ────────────────────────────────────────────────

const TOKEN_KEY = 'udisys_token';
const USER_KEY  = 'udisys_user';

// ── Context ───────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<UdisysUser | null>(null);
  const [token,   setToken]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Restaura sessão salva ao carregar o app
  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const savedUser  = localStorage.getItem(USER_KEY);

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  // ── Consultar empresas disponíveis (sem gerar JWT) ───────────────────────
  const getEmpresasDisponiveis = async (
    login: string,
    senha: string,
  ): Promise<{ empresas: EmpresaInfo[]; error: Error | null; bloqueio?: BloqueioAssinatura }> => {
    try {
      const { data } = await apiClient.post('/api/auth/empresas', { login, senha });
      return { empresas: data.empresas ?? [], error: null };
    } catch (err) {
      // 402/403 do gate de assinatura: credenciais OK, mas trial vencido /
      // assinatura suspensa ou cancelada — o chamador mostra o card com a ação.
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as
          { codigo?: string; mensagem?: string; checkoutUrl?: string | null } | undefined;
        if (
          data?.codigo &&
          ['TRIAL_EXPIRADO', 'ASSINATURA_SUSPENSA', 'ASSINATURA_CANCELADA'].includes(data.codigo)
        ) {
          return {
            empresas: [],
            error: null,
            bloqueio: {
              codigo: data.codigo as BloqueioAssinatura['codigo'],
              mensagem: data.mensagem ?? 'Assinatura pendente.',
              checkoutUrl: data.checkoutUrl,
            },
          };
        }
      }
      return { empresas: [], error: new Error(getApiErrorMessage(err, 'Credenciais inválidas.')) };
    }
  };

  // ── Login ────────────────────────────────────────────────────────────────
  const signIn = async (
    login: string,
    senha: string,
    empresaId?: number,
  ): Promise<{ error: Error | null }> => {
    try {
      const { data } = await apiClient.post('/api/auth/login', { login, senha, empresaId });

      // A API retorna: { accessToken, expiracao, usuario: { id, login, nome, email, tenantId, empresa, perfis } }
      const { accessToken, usuario } = data as {
        accessToken: string;
        usuario: UdisysUser;
      };

      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(usuario));

      setToken(accessToken);
      setUser(usuario);
      navigate('/');

      return { error: null };
    } catch (err) {
      return { error: new Error(getApiErrorMessage(err, 'Credenciais inválidas.')) };
    }
  };

  // ── Empresas do usuário logado (seletor de troca) ────────────────────────
  const minhasEmpresas = async (): Promise<EmpresaInfo[]> => {
    try {
      const { data } = await apiClient.get('/api/auth/minhas-empresas');
      return data.empresas ?? [];
    } catch {
      return [];
    }
  };

  // ── Trocar empresa em sessão (sem redigitar senha) ───────────────────────
  const trocarEmpresa = async (empresaId: number): Promise<{ error: Error | null }> => {
    try {
      const { data } = await apiClient.post('/api/auth/trocar-empresa', { empresaId });
      const { accessToken, usuario } = data as { accessToken: string; usuario: UdisysUser };
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(usuario));
      setToken(accessToken);
      setUser(usuario);
      // Recarrega para re-escopar TODOS os dados pela nova empresa.
      window.location.reload();
      return { error: null };
    } catch (err) {
      return { error: new Error(getApiErrorMessage(err, 'Não foi possível trocar de empresa.')) };
    }
  };

  // ── Logout ───────────────────────────────────────────────────────────────
  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    navigate('/auth');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, signIn, signOut, getEmpresasDisponiveis, minhasEmpresas, trocarEmpresa }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
